import React, { useEffect, useState } from 'react';
import { crmApi } from '../api/crm';
import ProductPicker from '../components/ProductPicker';
import ContactPicker from '../components/ContactPicker';
import { useToast } from '../hooks/useToast';

const SOURCES = [['walk_in','Walk-in'],['phone','Phone'],['whatsapp','WhatsApp'],['referral','Referral'],['other','Other']];
const LOST = ['Price too high','Not in stock','Went elsewhere','Changed mind','No response','Other'];
const TABS = [['','All'],['new','New'],['contacted','Contacted'],['quoted','Quoted'],['won','Won'],['lost','Lost']];

const Enquiries: React.FC = () => {
  const { showToast } = useToast();
  const [rows, setRows] = useState<any[]>([]);
  const [tab, setTab] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [open, setOpen] = useState<any>(null);
  const [form, setForm] = useState<any>({ name:'', mobile:'', requirement:'', source:'walk_in', est_value:'', next_action_date:'', product_id:'', product_name:'' });
  const [fu, setFu] = useState<any>({ action_date:new Date().toISOString().split('T')[0], channel:'call', note:'', next_action_date:'' });

  const fmt = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
  const badge = (s:string) => ({ new:'badge-blue', contacted:'badge-amber', quoted:'badge-purple', won:'badge-green', lost:'badge-red' } as any)[s] || 'badge-blue';

  const load = () => {
    setLoading(true);
    crmApi.enquiries({ status: tab || undefined, search: search || undefined })
      .then(r => setRows(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [tab]); // eslint-disable-line

  const save = async () => {
    if (!form.name.trim() || !form.requirement.trim()) { showToast('Name and what they want are required', 'error'); return; }
    setSaving(true);
    try {
      await crmApi.createEnquiry({ ...form, est_value: Number(form.est_value || 0), product_id: form.product_id ? Number(form.product_id) : null,
        next_action_date: form.next_action_date || null });
      showToast('Enquiry recorded');
      setShowNew(false);
      setForm({ name:'', mobile:'', requirement:'', source:'walk_in', est_value:'', next_action_date:'', product_id:'', product_name:'' });
      load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
    finally { setSaving(false); }
  };

  const openOne = async (id:number) => {
    try { const r = await crmApi.getEnquiry(id); setOpen(r.data.data); setFu({ action_date:new Date().toISOString().split('T')[0], channel:'call', note:'', next_action_date:'' }); }
    catch { showToast('Could not open the enquiry', 'error'); }
  };

  const addFollowUp = async () => {
    if (!fu.note.trim()) { showToast('Write what happened', 'error'); return; }
    try {
      await crmApi.followUp({ ...fu, enquiry_id: open.id, next_action_date: fu.next_action_date || null });
      showToast('Follow-up recorded');
      openOne(open.id); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
  };

  const setStatus = async (status:string) => {
    let lost_reason = null, lost_note = null;
    if (status === 'lost') {
      lost_reason = window.prompt('Why was it lost?\n\n' + LOST.map((l,i)=>`${i+1}. ${l}`).join('\n') + '\n\nType the reason:');
      if (lost_reason === null) return;
      if (/^[1-6]$/.test(lost_reason)) lost_reason = LOST[Number(lost_reason)-1];
      lost_note = window.prompt('Anything to add? (optional)') || null;
    }
    try {
      await crmApi.updateEnquiry(open.id, { status, lost_reason, lost_note });
      showToast('Enquiry marked ' + status);
      openOne(open.id); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not update', 'error'); }
  };

  const th: React.CSSProperties = { padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' };

  // ---- one enquiry ----
  if (open) return (
    <div>
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => { setOpen(null); load(); }}>Back</button>
        <div style={{ display:'flex', gap:8 }}>
          {open.status !== 'won' && open.status !== 'lost' && <>
            <button className="btn btn-secondary" onClick={() => setStatus('quoted')}>Mark quoted</button>
            <button className="btn btn-primary" onClick={() => setStatus('won')}>Won</button>
            <button className="btn btn-secondary" onClick={() => setStatus('lost')}>Lost</button>
          </>}
        </div>
      </div>
      <div className="card" style={{ marginBottom:12 }}>
        <div className="card-header">
          <span>{open.enquiry_no} — {open.name}</span>
          <span className={'badge ' + badge(open.status)}>{open.status}</span>
        </div>
        <div className="card-body">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:10, fontSize:13 }}>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Mobile</div>{open.mobile || '-'}</div>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Source</div>{(SOURCES.find(s=>s[0]===open.source)||['','-'])[1]}</div>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Value</div>{fmt(open.est_value)}</div>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Handled by</div>{open.assigned_name || '-'}</div>
            <div><div style={{fontSize:11,color:'var(--tx3)'}}>Next action</div>{open.next_action_date || '-'}</div>
            {open.product_name && <div><div style={{fontSize:11,color:'var(--tx3)'}}>Product</div>{open.product_name}</div>}
            {open.sale_no && <div><div style={{fontSize:11,color:'var(--tx3)'}}>Sale</div>{open.sale_no}</div>}
          </div>
          <div style={{ marginTop:12, paddingTop:10, borderTop:'1px solid var(--bdr)' }}>
            <div style={{fontSize:11,color:'var(--tx3)'}}>What they want</div>
            <div style={{ fontSize:13 }}>{open.requirement}</div>
          </div>
          {open.lost_reason && <div style={{ marginTop:10, fontSize:13, color:'var(--red)' }}>Lost: {open.lost_reason}{open.lost_note ? ' — ' + open.lost_note : ''}</div>}
        </div>
      </div>

      <div className="card" style={{ marginBottom:12 }}>
        <div className="card-header"><span>Record a follow-up</span></div>
        <div className="card-body">
          <div className="form-grid-2">
            <div className="form-group"><label className="form-label">Date</label>
              <input type="date" className="form-control" value={fu.action_date} onChange={e => setFu({...fu, action_date:e.target.value})} /></div>
            <div className="form-group"><label className="form-label">How</label>
              <select className="form-control" value={fu.channel} onChange={e => setFu({...fu, channel:e.target.value})}>
                <option value="call">Call</option><option value="visit">Visit</option>
                <option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="other">Other</option>
              </select></div>
          </div>
          <div className="form-group"><label className="form-label">What happened *</label>
            <textarea className="form-control" rows={2} value={fu.note} onChange={e => setFu({...fu, note:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Next action date</label>
            <input type="date" className="form-control" style={{maxWidth:200}} value={fu.next_action_date} onChange={e => setFu({...fu, next_action_date:e.target.value})} /></div>
          <button className="btn btn-primary" onClick={addFollowUp}>Save follow-up</button>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span>History</span><span className="record-count">{open.follow_ups?.length || 0}</span></div>
        {(open.follow_ups || []).length === 0
          ? <div className="empty-state"><p>No follow-ups yet.</p></div>
          : <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--sur2)' }}>
                <th style={th}>Date</th><th style={th}>How</th><th style={th}>Note</th><th style={th}>Next</th><th style={th}>By</th>
              </tr></thead>
              <tbody>
                {open.follow_ups.map((f:any) => (
                  <tr key={f.id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                    <td style={{ padding:'8px 10px', fontSize:12 }}>{f.action_date}</td>
                    <td style={{ padding:'8px 10px', fontSize:12 }}>{f.channel}</td>
                    <td style={{ padding:'8px 10px', fontSize:12 }}>{f.note}</td>
                    <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx3)' }}>{f.next_action_date || '-'}</td>
                    <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>{f.by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
    </div>
  );

  // ---- new enquiry ----
  if (showNew) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">New Enquiry</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setShowNew(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
      <div className="card"><div className="card-body">
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Name *</label>
            <input className="form-control" value={form.name} onChange={e => setForm({...form, name:e.target.value})} autoFocus /></div>
          <div className="form-group"><label className="form-label">Mobile</label>
            <input className="form-control mono" value={form.mobile} onChange={e => setForm({...form, mobile:e.target.value})} /></div>
        </div>
        <div className="form-group"><label className="form-label">What are they asking for? *</label>
          <textarea className="form-control" rows={2} value={form.requirement} onChange={e => setForm({...form, requirement:e.target.value})} /></div>
        <div className="form-group"><label className="form-label">Product (optional)</label>
          <ProductPicker value={form.product_id} initialLabel={form.product_name}
            onSelect={(p:any) => setForm({...form, product_id:String(p.id), product_name:p.name})} /></div>
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Where did they come from?</label>
            <select className="form-control" value={form.source} onChange={e => setForm({...form, source:e.target.value})}>
              {SOURCES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div className="form-group"><label className="form-label">Value (optional)</label>
            <input className="form-control mono" style={{textAlign:'right'}} value={form.est_value} onChange={e => setForm({...form, est_value:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Call back on</label>
            <input type="date" className="form-control" value={form.next_action_date} onChange={e => setForm({...form, next_action_date:e.target.value})} /></div>
        </div>
      </div></div>
    </div>
  );

  // ---- list ----
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Enquiries</h1>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Enquiry</button>
      </div>
      <div className="tabs" style={{ marginBottom:12, display:'flex', gap:6, flexWrap:'wrap' }}>
        {TABS.map(([v,l]) => (
          <button key={v} className={'btn btn-sm ' + (tab===v ? 'btn-primary' : 'btn-secondary')} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>
      <div className="filter-bar" style={{ marginBottom:12, display:'flex', gap:8 }}>
        <input className="form-control" style={{ maxWidth:300 }} placeholder="Search name, mobile or what they want..."
          value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key==='Enter') load(); }} />
        <button className="btn btn-secondary btn-sm" onClick={load}>Search</button>
        <span className="record-count" style={{ marginLeft:'auto' }}>{rows.length}</span>
      </div>
      <div className="card">
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'var(--sur2)' }}>
            <th style={th}>No</th><th style={th}>Name</th><th style={th}>Mobile</th><th style={th}>Wants</th>
            <th style={{...th, textAlign:'right'}}>Value</th><th style={th}>Next action</th><th style={th}>Status</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>Loading...</td></tr>
            : rows.length === 0 ? <tr><td colSpan={7} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>No enquiries yet.</td></tr>
            : rows.map(r => (
              <tr key={r.id} onClick={() => openOne(r.id)} style={{ borderBottom:'1px solid var(--bdr)', cursor:'pointer' }}>
                <td style={{ padding:'8px 10px', fontSize:12, fontWeight:600, color:'var(--blue)' }}>{r.enquiry_no}</td>
                <td style={{ padding:'8px 10px', fontSize:12 }}>{r.name}</td>
                <td style={{ padding:'8px 10px', fontSize:12, fontFamily:'monospace' }}>{r.mobile || '-'}</td>
                <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx2)' }}>{String(r.requirement).slice(0,46)}</td>
                <td className="td-num" style={{ padding:'8px 10px' }}>{Number(r.est_value) ? fmt(r.est_value) : '-'}</td>
                <td style={{ padding:'8px 10px', fontSize:12, color: r.overdue ? 'var(--red)' : 'var(--tx3)' }}>{r.next_action_date || '-'}</td>
                <td style={{ padding:'8px 10px' }}><span className={'badge ' + badge(r.status)}>{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Enquiries;
